// Formatting utilities for the Expertise Detail component

// Format currency amounts
export const formatCurrency = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '€ 0,00';
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) return '€ 0,00';
  
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

// Format dates
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '-';
  
  return new Intl.DateTimeFormat('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

// Format date and time
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '-';
  
  return new Intl.DateTimeFormat('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

// Format relative time (e.g., "2 hours ago")
export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return 'À l\'instant';
  } else if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  } else if (diffDays < 7) {
    return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  } else {
    return formatDate(dateObj);
  }
};

// Format phone numbers
export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // Belgian phone number format
  if (cleaned.startsWith('32')) {
    // International format: +32 XXX XX XX XX
    const match = cleaned.match(/^(32)(\d{3})(\d{2})(\d{2})(\d{2})$/);
    if (match) {
      return `+${match[1]} ${match[2]} ${match[3]} ${match[4]} ${match[5]}`;
    }
  } else if (cleaned.startsWith('0')) {
    // Local format: 0XXX XX XX XX
    const match = cleaned.match(/^(0\d{3})(\d{2})(\d{2})(\d{2})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }
  }
  
  return phone;
};

// Format address
export const formatAddress = (
  street?: string,
  number?: string,
  postcode?: string,
  city?: string
): string => {
  const parts = [];
  
  if (number && street) {
    parts.push(`${street} ${number}`);
  } else if (street) {
    parts.push(street);
  }
  
  if (postcode && city) {
    parts.push(`${postcode} ${city}`);
  } else if (city) {
    parts.push(city);
  }
  
  return parts.join(', ') || '-';
};

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format percentage
export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

// Format expertise number
export const formatExpertiseNumber = (id: string | number): string => {
  const idStr = String(id);
  // If it already starts with EXP-, return as is
  if (idStr.toUpperCase().startsWith('EXP-')) {
    return idStr.toUpperCase();
  }
  // Otherwise add the prefix
  return `EXP-${idStr}`;
};

// Truncate text with ellipsis
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

// Capitalize first letter
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Format name (firstName lastName)
export const formatName = (firstName?: string, lastName?: string): string => {
  const parts = [];
  if (firstName) parts.push(firstName);
  if (lastName) parts.push(lastName);
  return parts.join(' ') || '-';
};

// Format document status
export const formatDocumentStatus = (status: string): { text: string; color: string } => {
  const statusMap: Record<string, { text: string; color: string }> = {
    requested: { text: 'Demandé', color: '#F59E0B' },
    checked: { text: 'Vérifié', color: '#10B981' },
    provided: { text: 'Fourni', color: '#3B82F6' },
    pending: { text: 'En attente', color: '#6B7280' },
  };
  
  return statusMap[status] || { text: status, color: '#6B7280' };
};

// Normalize document title for comparison (used in fuzzy matching)
export const normalizeDocumentTitle = (title: string): string => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, ''); // Remove non-alphanumeric
};

// Check if two document titles are similar (fuzzy matching)
export const areDocumentTitlesSimilar = (title1: string, title2: string): boolean => {
  const normalized1 = normalizeDocumentTitle(title1);
  const normalized2 = normalizeDocumentTitle(title2);
  
  // Exact match after normalization
  if (normalized1 === normalized2) return true;
  
  // Check if one contains the other (with at least 70% length)
  const minLength = Math.min(normalized1.length, normalized2.length);
  const maxLength = Math.max(normalized1.length, normalized2.length);
  
  if (minLength / maxLength < 0.7) return false;
  
  return normalized1.includes(normalized2) || normalized2.includes(normalized1);
};